import React, { useEffect, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../../Lib/utils.js";
import { Button } from "../../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { getStudents } from "../../../provider/adm/EstudiantePersonalizado/getStudents";

const ScrollListStudents = ({
  studentSelectedToFilter,
  setStudentSelectedToFilter,
  setLoading,
  setStudentID,
  multiSelect = false,
  disabled = false, // Nueva prop
  onStudentListChange,
}) => {
  const [open, setOpen] = useState(false);
  const [listaStudents, setListaStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Función mejorada para comparar nombres ignorando espacios extras
  const compareNames = (name1, name2) => {
    if (!name1 || !name2) return false;
    // Normalizar: trim + convertir espacios múltiples a uno solo + lowercase para comparación
    const normalize = (str) =>
      str.trim().replace(/\s+/g, " ").toLowerCase();
    return normalize(name1) === normalize(name2);
  };

  // Normalizar selectedStudents para que siempre sea consistente
  const selectedStudents = multiSelect
    ? Array.isArray(studentSelectedToFilter)
      ? studentSelectedToFilter
      : studentSelectedToFilter
      ? [studentSelectedToFilter]
      : []
    : studentSelectedToFilter;

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const students = await getStudents(true);
        setListaStudents(students || []);
        if (onStudentListChange) {
          onStudentListChange(students || []);
        }
      } catch (error) {
        console.error('Error fetching students:', error);
        setListaStudents([]);
        if (onStudentListChange) {
          onStudentListChange([]); // Llamar al callback con una lista vacía en caso de error
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [setLoading]);

  // Helper function to handle selection based on mode
  const handleSelect = (currentValue, studentId) => {
    if (multiSelect) {
      const currentSelection = Array.isArray(studentSelectedToFilter)
        ? studentSelectedToFilter
        : studentSelectedToFilter
        ? [studentSelectedToFilter]
        : [];

      // Buscar si ya está seleccionado usando comparación mejorada
      const isAlreadySelected = currentSelection.some((selected) =>
        compareNames(selected, currentValue)
      );

      if (isAlreadySelected) {
        // Remove if already selected - usar comparación mejorada
        const newSelection = currentSelection.filter((name) =>
          !compareNames(name, currentValue)
        );
        setStudentSelectedToFilter(newSelection);
      } else {
        // Add to selection
        const newSelection = [...currentSelection, currentValue];
        setStudentSelectedToFilter(newSelection);
      }
    } else {
      // Original single select behavior - usar comparación mejorada
      const newValue = compareNames(currentValue, studentSelectedToFilter)
        ? ""
        : currentValue;
      setStudentSelectedToFilter(newValue);
      if (setStudentID) {
        setStudentID(newValue ? studentId : null);
      }
      setOpen(false);
    }
  };

  const filteredStudents = listaStudents.filter((student) => {
    const searchableFields = [
      student.fullName, 
      student.email, 
      student.ID?.toString(),
      student.phoneNumber
    ];
    return searchableFields.some(
      (field) =>
        field &&
        typeof field === "string" &&
        field.toLowerCase().includes(searchTerm.toLowerCase().trim())
    );
  });

  const getDisplayText = () => {
    if (multiSelect) {
      if (Array.isArray(selectedStudents) && selectedStudents.length > 0) {
        return `${selectedStudents.length} estudiantes seleccionados`;
      }
      return "Selecciona estudiantes...";
    } else {
      if (studentSelectedToFilter) {
        // Buscar el estudiante usando comparación mejorada
        const found = listaStudents.find((student) =>
          compareNames(student.fullName, studentSelectedToFilter)
        );

        if (found) {
          return found.fullName;
        } else {
          // Si no se encuentra, mostrar el valor seleccionado directamente
          return studentSelectedToFilter;
        }
      }
      return "Selecciona estudiante...";
    }
  };

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger>
        <Button
          variant="outline"
          role="combobox"
          type="button"
          className={`w-[250px] justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={disabled}
        >
          {disabled ? "Todos los estudiantes" : getDisplayText()}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command>
          <input
            placeholder="Buscar estudiante..."
            className="h-9 px-3 border-b"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <CommandList>
            <CommandEmpty>No se encontraron estudiantes.</CommandEmpty>
            <CommandGroup>
              {filteredStudents.map((student) => (
                <CommandItem
                  key={student.ID}
                  value={student.fullName}
                  onSelect={(currentValue) => {
                    handleSelect(currentValue, student.ID);
                  }}
                  className="flex items-center py-2"
                >
                  {multiSelect && (
                    <input
                      type="checkbox"
                      checked={
                        Array.isArray(selectedStudents) &&
                        selectedStudents.some((selected) =>
                          compareNames(selected, student.fullName)
                        )
                      }
                      onChange={() => {}}
                      className="h-4 w-4 mr-2"
                    />
                  )}
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{student.fullName}</span>
                      <span className="text-xs text-gray-400 bg-gray-100 px-1 py-0.5 rounded">
                        ID: {student.ID}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 truncate">
                      {student.email}
                    </span>
                  </div>
                  {!multiSelect && (
                    <Check
                      className={cn(
                        "ml-2 h-4 w-4",
                        compareNames(studentSelectedToFilter, student.fullName)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ScrollListStudents;